import React, { useState } from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@3plearning/react-components';
import { t } from '@lingui/macro';
import Resources from '../../../../../helpers/resources';
import { showToast } from '../../../../toast/toast-action';
import {
  ERROR_DETECTED,
  UPDATE_SORT_PROPERTIES,
  UPDATE_SORTED_ASSIGNED_LIST
} from '../../../../../containers/course-assign/course-assign-types';
import { DISMISS_MODAL } from '../../../../../containers/modal-manager/modal-manager-actions';
import { getTopics } from '../../../../../containers/course-assign/course-assign-actions';
import { CHECKED, UNCHECKED, DISABLED } from '../../../../checkbox/constants';
import InstructionHubFeedback from '../../../../instruction-hub-feedback';
import { INSTRUCTION_HUB_MESSAGE_CONTEXTS } from '../../../../instruction-hub-message';
import {
  getInstructionHubAssignmentsConfig,
  isFullyInstructionHubBlocked,
  isInstructionHubAssignmentRecord
} from '../../../../../helpers/instruction-hub/instruction-hub-assignments';
import AssignedListItem from '../assigned-list-item/assigned-list-item';
import Checkbox from '../../../../checkbox';
import { customSort } from '../../../student-list-util';
import {
  DownArrow,
  StudentIcon,
  UpArrow
} from '../../../../../helpers/icon-dictionary';
import SvgIcon from '../../../../svg-icon/svg-icon';
import { isDefined } from '../../../../../helpers/utils';
import {
  ResultsWrapper,
  AssignedListWrapper,
  Body,
  Footer,
  OkButtonContainer,
  UnAssignButtonContainer,
  NoDataView,
  AssignedWrapper,
  ActivityHeader,
  CheckboxWrapper,
  ActivityTitle,
  HeaderContainer,
  SvgWrapper,
  StatusHeader
} from './assigned-list.styled';

const AssignedList = ({ i18n, assignmentList, studentName }) => {
  const dispatch = useDispatch();
  const [direction, setDirection] = useState('desc');
  const [activityList, setActivityList] = useState(assignmentList);
  const [unAssignedList, setUnAssignedList] = useState({});
  const [sortingProp, setSortingProp] = useState('direction');
  const [selectAllValue, setSelectAllValue] = useState(false);
  const showToastText = i18n
    ._(t('assignedTab.unassignSuccess')`activities are unassigned from`)
    .concat(' ', studentName);

  const classroomId = useSelector(
    (state) => state.CourseAssignReducer.activeClass
  );

  const groupId = useSelector((state) => state.CourseAssignReducer.activeGroup);

  const sortDirection = useSelector(
    (state) => state.CourseAssignReducer.sortDirection
  );

  const instructionHubConfig = getInstructionHubAssignmentsConfig();
  const isRowInstructionHubBlocked = (assignment) =>
    isInstructionHubAssignmentRecord(assignment, instructionHubConfig);
  const selectableCount = activityList.filter(
    (assignment) => !isRowInstructionHubBlocked(assignment)
  ).length;
  const selectedActivities = Object.values(unAssignedList).filter(isDefined);
  const selectAllState = selectAllValue ? CHECKED : UNCHECKED;
  const isAllInstructionHubBlocked = isFullyInstructionHubBlocked(
    activityList.length - selectableCount,
    activityList.length
  );

  function handleSuccess() {
    dispatch(getTopics(classroomId, groupId));
  }

  const handleOnUnassign = () => {
    const assignedTaskIdList = selectedActivities.map(
      ({ assignedTaskId }) => assignedTaskId
    );

    Resources.unassign
      .put({ classroomId, groupId })
      .withBody({
        assignedTaskIdList
      })
      .send()
      .then(
        () => {
          handleSuccess();
          dispatch(
            showToast(
              `${assignedTaskIdList.length} ${showToastText}`,
              'success'
            )
          );
        },
        (err) => {
          dispatch({
            type: ERROR_DETECTED,
            payload: err
          });
        }
      );
    dispatch({ type: DISMISS_MODAL });
  };

  const sortHandler = () => {
    dispatch({
      type: UPDATE_SORT_PROPERTIES,
      direction: sortDirection === 'asc' ? 'desc' : 'asc',
      sortValue: 'activityName'
    });

    if (direction === 'asc') {
      setDirection('desc');
    } else {
      setDirection('asc');
    }

    const sortedList = customSort(
      activityList,
      'activityName',
      sortDirection === 'asc' ? 'desc' : 'asc'
    );
    setActivityList(sortedList);
    dispatch({
      type: UPDATE_SORTED_ASSIGNED_LIST,
      activityList: sortedList
    });
    setSortingProp('direction');
  };

  const handleSortingArrow = (dir, isActive) => {
    const icon =
      dir === 'asc' ? <SvgIcon icon={DownArrow} /> : <SvgIcon icon={UpArrow} />;
    return <SvgWrapper status={isActive}>{icon}</SvgWrapper>;
  };

  // To select all the activities
  const selectAllCheckboxHandler = () => {
    const filteredActivityIds = {
      ...unAssignedList
    };
    const newSelectValue = !selectAllValue;

    for (const index in activityList) {
      if (newSelectValue && !isRowInstructionHubBlocked(activityList[index])) {
        filteredActivityIds[index] = activityList[index];
      } else {
        filteredActivityIds[index] = undefined;
      }
    }
    setUnAssignedList(filteredActivityIds);
    // Reverse the value of the select all
    setSelectAllValue(newSelectValue);
  };

  // To select individual activity
  const individualCheckboxHandler = (actionType, index) => {
    const filteredActivityIds = {
      ...unAssignedList,
      [index]: actionType === 'checked' ? activityList[index] : undefined
    };

    setUnAssignedList(filteredActivityIds);
    const tempSelectedActivity = Object.values(
      filteredActivityIds
    ).filter((item) => isDefined(item));

    // test if the checkbox conflicts with the select all
    if (selectableCount !== tempSelectedActivity.length) {
      setSelectAllValue(false);
    }
  };

  return (
    <AssignedWrapper id="activityList">
      <ResultsWrapper>
        <HeaderContainer>
          <ActivityHeader>
            <CheckboxWrapper>
              <InstructionHubFeedback
                i18n={i18n}
                context={INSTRUCTION_HUB_MESSAGE_CONTEXTS.STUDENT}
                isBlocked={isAllInstructionHubBlocked}
              >
                <Checkbox
                  id="select-all-checkbox"
                  checkboxHandler={selectAllCheckboxHandler}
                  state={isAllInstructionHubBlocked ? DISABLED : selectAllState}
                />
              </InstructionHubFeedback>
            </CheckboxWrapper>
            <ActivityTitle>
              <span
                data-testid="assignedTab-title"
                className="title"
                role="button"
                tabIndex="-1"
                onClick={sortHandler}
              >
                <span className="label" data-testid="name-label">
                  {i18n._(t('assignedTab.Activity')`Activity`)}
                </span>
                {handleSortingArrow(sortDirection, sortingProp === 'direction')}
              </span>
            </ActivityTitle>
          </ActivityHeader>
          <StatusHeader>
            <span data-testid="assignedTab-status">
              {i18n._(t('assignedTab.Status')`Status`)}
            </span>
          </StatusHeader>
          <StatusHeader>
            <span data-testid="assignedTab-started">
              {i18n._(t('assignedTab.Started')`Started`)}
            </span>
          </StatusHeader>
          <StatusHeader>
            <span data-testid="assignedTab-due">
              {i18n._(t('assignedTab.Due')`Due`)}
            </span>
          </StatusHeader>
        </HeaderContainer>
        {activityList.length ? (
          <AssignedListWrapper data-testid="assigned-list">
            <Body>
              {activityList.map((row, index) => {
                const checkboxChecked =
                  typeof unAssignedList[index] === 'object';
                return (
                  <AssignedListItem
                    i18n={i18n}
                    key={row.assignedTaskId}
                    index={index}
                    onCheckboxChange={individualCheckboxHandler}
                    assignedListItem={row}
                    isSelected={checkboxChecked}
                    isInstructionHubBlocked={isRowInstructionHubBlocked(row)}
                  />
                );
              })}
            </Body>
          </AssignedListWrapper>
        ) : (
          <NoDataView data-testid="no-data-view">
            <Isvg src={StudentIcon} cacheGetRequests />
            <p>
              {i18n._(
                t(
                  'assignedTab.noDataMainParagraph'
                )`There are currently no Course activities assigned to this student.`
              )}
            </p>
            <p>
              {i18n._(
                t(
                  'assignedTab.noDataSubParagraph'
                )`Once assigned, you can quickly monitor the status of activities here.`
              )}
            </p>
          </NoDataView>
        )}
      </ResultsWrapper>
      <Footer data-testid="footer">
        <div></div>
        <UnAssignButtonContainer>
          <Button
            className="unassign"
            primary
            text={i18n._(t('assignedTab.UnassignButton')`Unassign`)}
            type={Button.LOZENGE}
            onClick={handleOnUnassign}
            disabled={selectedActivities.length === 0}
          />
        </UnAssignButtonContainer>
        <OkButtonContainer>
          <Button
            className="button"
            primary
            text={i18n._(t('assignedTab.OkButton')`OK`)}
            type={Button.LOZENGE}
            onClick={() => {
              dispatch({ type: DISMISS_MODAL });
            }}
          />
        </OkButtonContainer>
      </Footer>
    </AssignedWrapper>
  );
};

AssignedList.propTypes = {
  i18n: PropTypes.object,
  assignmentList: PropTypes.array,
  studentName: PropTypes.string
};

export default AssignedList;



// WEBPACK FOOTER //
// ./components/student-list/student-modal/student-assigned/assigned-list/assigned-list.js